import * as THREE from 'three/webgpu';
import {PointSnapManager} from '../../../manager/PointSnapManager';
import type { PointSnapResult } from '../../../manager/pointSnap';
import type { Viewer } from '../../../viewer/Viewer';
import type { SketchEntitiesTransformGeometryService } from '../SketchEntitiesTransformGeometryService';

//------ 草图实体变换参考点捕捉 开始------
/**
 * 草图实体变换点捕捉解析结果。
 */
export interface SketchEntitiesTransformPointSnapResolution {
    /** 捕捉后的世界坐标。 */
    readonly position: THREE.Vector3;
    /** 捕捉结果展示标签。 */
    readonly label: string;
    /** 原始点捕捉结果。 */
    readonly snapResult: PointSnapResult;
}

/**
 * 草图实体变换点捕捉排除集构造输入。
 */
export interface SketchEntitiesTransformPointSnapExclusionInput {
    /** 当前命令已选实体。 */
    readonly entities: readonly THREE.Object3D[];
    /** 额外需要排除的运行时对象，例如复制实体上一帧预览副本。 */
    readonly extraEntities?: readonly THREE.Object3D[];
    /** 草图实体变换几何服务，用于复用唯一变换点收集规则。 */
    readonly geometryService: SketchEntitiesTransformGeometryService;
}

/**
 * 解析草图实体变换命令中的点捕捉。
 *
 * @param input 捕捉解析输入。
 * @returns 命中的点捕捉结果；未命中时返回 null。
 *
 * 设计说明：
 * 1. Move / Copy 的 From 起点和 Rotate 的旋转中心应复用草图绘制的捕捉手感；
 * 2. 这里只负责构造与草图绘制一致的 PointSnapManager 请求，不直接调用具体策略；
 * 3. 屏幕坐标必须转换为 renderer 像素坐标，否则策略中的像素阈值会失准；
 * 4. 未命中时主动隐藏捕捉指示器，由调用方继续回退到草图平面自由点。
 */
export function resolveSketchEntitiesTransformPointSnap(input: {
    /** Viewer 实例。 */
    readonly viewer: Viewer;
    /** 鼠标屏幕 X 坐标。 */
    readonly clientX: number;
    /** 鼠标屏幕 Y 坐标。 */
    readonly clientY: number;
    /** 鼠标在草图平面上的自由投影点。 */
    readonly worldPoint: THREE.Vector3;
    /** 当前捕捉需要排除的目标对象 ID 集合。 */
    readonly excludedTargetObjectIds?: ReadonlySet<string>;
}): SketchEntitiesTransformPointSnapResolution | null {
    const snapManager = input.viewer.pointSnapManager;
    if (!snapManager) {
        return null;
    }

    // 浏览器坐标转换、策略调用与指示器反馈由公共管理器统一处理，避免 Move / Copy 各自维护一套链路。
    const snapResult = snapManager.resolvePointerSnap(
        input.clientX,
        input.clientY,
        input.worldPoint,
        input.excludedTargetObjectIds,
    );

    if (!snapResult) {
        return null;
    }

    return {
        position: snapResult.position.clone(),
        label: resolvePointSnapLabel(snapResult),
        snapResult,
    };
}

/**
 * 创建草图实体变换预览阶段的点捕捉排除 ID 集合。
 *
 * @param input 排除集构造输入。
 * @returns 需要从点捕捉候选中排除的对象 ID 集合。
 *
 * 设计说明：
 * 1. Move / Copy 预览阶段仍要捕捉其它对象上的点；旋转中心选择当前不需要构造排除域；
 * 2. 当前正在移动/复制的源对象、上一帧复制预览副本及其控制点必须排除；
 * 3. 使用 `collectUniquePoints` 复用变换服务的点域定义，避免捕捉排除域与真实变换域不一致。
 */
export function createSketchEntitiesTransformPointSnapExclusionIds(
    input: SketchEntitiesTransformPointSnapExclusionInput,
): ReadonlySet<string> {
    const entities = [...input.entities, ...(input.extraEntities ?? [])];
    const excludedIds = new Set(PointSnapManager.createExcludedTargetObjectIds(entities));
    input.geometryService.collectUniquePoints(entities).forEach((point) => {
        excludedIds.add(point.uuid);
    });
    return excludedIds;
}

/**
 * 清理草图实体变换命令中的点捕捉反馈。
 *
 * @param viewer Viewer 实例。
 *
 * 设计说明：
 * 1. Move / Copy 预览和 Rotate 旋转中心选择会显示点捕捉图标与策略级预览对象；
 * 2. 命令确认、取消或清理时必须主动收口，否则右下角图标或中点/象限点/HV 引导线会残留；
 * 3. 统一放在实体变换捕捉工具中，避免各 controller 重复理解 PointerNearbyLabelManager 槽位细节。
 */
export function clearSketchEntitiesTransformPointSnapFeedback(viewer: Viewer): void {
    viewer.pointSnapManager?.clearPointerSnapFeedback();
}

/**
 * 解析捕捉结果展示标签。
 *
 * @param snapResult 点捕捉结果。
 * @returns PropertyManager 中可展示的短标签。
 */
function resolvePointSnapLabel(snapResult: PointSnapResult): string {
    const objectName = snapResult.targetObject?.name?.trim();
    return objectName && objectName.length > 0 ? objectName : '点捕捉';
}

//------ 草图实体变换参考点捕捉 结束------
